import {
  bindingSourceEnum,
  bindingSourceFields,
  BindingSourceValue,
  Conversation,
  TemplateVariableBinding,
} from './models'

// Allows a dot so a canonical token like `reservation.checkin_date` (inserted by
// the template-variable-picker) parses as one variable, not a name that breaks at
// the dot. Mirrors the server's `_PLACEHOLDER_RE` in whatsapp/templates.py.
const PLACEHOLDER_RE = /{{\s*([\w.]+)\s*}}/g

/** `display_name` -> `Display name`. Field names are the backend's, so there is
 * no i18n catalogue to look them up in. */
export function humanize(name: string): string {
  const words = name.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

// i18n keys for fields whose humanized name reads poorly (`Checkin time`).
const BINDING_FIELD_LABEL_KEYS: Record<string, string> = {
  checkin_date: 'whatsappTemplates.bindingFieldCheckinDate',
  checkin_time: 'whatsappTemplates.bindingFieldCheckinTime',
  checkout_date: 'whatsappTemplates.bindingFieldCheckoutDate',
  checkout_time: 'whatsappTemplates.bindingFieldCheckoutTime',
  formatted_address: 'whatsappTemplates.bindingFieldFormattedAddress',
  state_or_region: 'whatsappTemplates.bindingFieldStateOrRegion',
  checkin_survey_link: 'whatsappTemplates.bindingFieldCheckinSurveyLink',
}

/** The check-in survey link: offered only for orgs with a TRA/SIRE-reporting
 * property, and never filled by hand — a typed URL can't stand in for the
 * guest's signed link. Mirrors the server's `NO_MANUAL_FALLBACK_FIELDS`. */
export const CHECKIN_SURVEY_LINK_TOKEN = 'reservation.checkin_survey_link'

export function isCheckinSurveyLink(binding: TemplateVariableBinding | undefined): boolean {
  return `${binding?.source}.${binding?.field}` === CHECKIN_SURVEY_LINK_TOKEN
}

/** Display label for a binding field: its translation when it has one,
 * otherwise the humanized field name. */
export function fieldLabel(field: string, t: (key: string) => string): string {
  const key = BINDING_FIELD_LABEL_KEYS[field]
  return key ? t(key) : humanize(field)
}

/** A `{{source.field}}` option the variable picker offers, and what it binds to. */
export interface CanonicalVariableOption {
  token: string
  source: Exclude<BindingSourceValue, 'manual'>
  field: string
  label: string
}

// Built once from `bindingSourceFields` — the single source of truth both the
// picker's list and the resolution logic key off, grouped in declaration order.
export const CANONICAL_VARIABLE_OPTIONS: CanonicalVariableOption[] = (
  Object.keys(bindingSourceFields) as Array<keyof typeof bindingSourceFields>
).flatMap((source) =>
  bindingSourceFields[source].map((field) => ({
    token: `${source}.${field}`,
    source,
    field,
    label: humanize(field),
  })),
)

const CANONICAL_OPTION_BY_TOKEN: Record<string, CanonicalVariableOption> = Object.fromEntries(
  CANONICAL_VARIABLE_OPTIONS.map((option) => [option.token, option]),
)

/** Canonical options whose token contains `query` (case-insensitive); all of them
 * for a blank query. The check-in survey link is listed only when the org has a
 * property reporting to TRA/SIRE. */
export function filterCanonicalOptions(
  query: string,
  { hasReportingProperties = false }: { hasReportingProperties?: boolean } = {},
): CanonicalVariableOption[] {
  const needle = query.trim().toLowerCase()
  return CANONICAL_VARIABLE_OPTIONS.filter(
    (option) =>
      (hasReportingProperties || option.token !== CHECKIN_SURVEY_LINK_TOKEN) &&
      option.token.includes(needle),
  )
}

/** The source/field a canonical token (e.g. `reservation.checkin_date`) binds to,
 * or `undefined` for a freehand name that doesn't match the canonical list. */
export function canonicalBindingFor(
  variable: string,
): { source: string; field: string } | undefined {
  const option = CANONICAL_OPTION_BY_TOKEN[variable]
  return option ? { source: option.source, field: option.field } : undefined
}

/**
 * If the cursor sits inside an unterminated `{{...}}` token (a `{{` with no `}}`
 * between it and the cursor), the partial text typed so far — the picker's filter
 * query. `undefined` when the cursor isn't inside one, so the picker stays closed.
 */
export function activeVariableQuery(body: string, cursor: number): string | undefined {
  const upToCursor = body.slice(0, cursor)
  const lastOpen = upToCursor.lastIndexOf('{{')
  if (lastOpen === -1) return undefined
  const between = upToCursor.slice(lastOpen + 2)
  if (between.includes('}}')) return undefined
  return between.trimStart()
}

/** Replace the open `{{...}}` token containing `cursor` with `{{token}}`, closing
 * it. Returns the new body and where the cursor should land, right after `}}`. */
export function insertCanonicalToken(
  body: string,
  cursor: number,
  token: string,
): { body: string; cursor: number } {
  const upToCursor = body.slice(0, cursor)
  const start = upToCursor.lastIndexOf('{{') + 2
  const newBody = `${body.slice(0, start)}${token}}}${body.slice(cursor)}`
  return { body: newBody, cursor: start + token.length + 2 }
}

/** Distinct `{{named}}` variables a template body references, in body order.
 * Collected through `replace` (not `matchAll`) to stay es5-safe and to avoid the
 * shared regex's `lastIndex` leaking between calls. */
export function variablesIn(body: string): string[] {
  const names: string[] = []
  body.replace(PLACEHOLDER_RE, (_placeholder, name: string) => {
    if (!names.includes(name)) names.push(name)
    return ''
  })
  return names
}

/** Index a template's bindings by their variable name for quick lookup. */
export function bindingsByVariable(
  bindings: TemplateVariableBinding[] | undefined,
): Record<string, TemplateVariableBinding> {
  const byVariable: Record<string, TemplateVariableBinding> = {}
  ;(bindings ?? []).forEach((binding) => {
    byVariable[binding.variable] = binding
  })
  return byVariable
}

// API/DB fields are snake_case; the conversation refs are camelCased by
// tn-models, so a binding's field name has to be converted before lookup.
/** The linked entities a binding resolves from: an existing conversation's refs,
 * or the recipient being picked before a conversation exists. */
export type BindingContext = Partial<
  Pick<Conversation, 'contactRef' | 'reservationRef' | 'guestRef'>
>

function snakeToCamel(field: string): string {
  return field.replace(/_([a-z])/g, (_match, char: string) => char.toUpperCase())
}

function nonEmpty(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined
  const text = String(value).trim()
  return text.length ? text : undefined
}

// Mirrors the server's `_format_language`: `es-CO` -> `es`; unknown -> `en`.
function localizedFor(
  localizedFields: Record<string, Record<string, string>> | null | undefined,
  language: string | undefined,
): Record<string, string> | undefined {
  if (!localizedFields) return undefined
  const base = (language ?? '').replace('_', '-').split('-')[0].toLowerCase()
  return localizedFields[base] ?? localizedFields.en
}

/**
 * Resolve a bound variable from the conversation's (or recipient's) linked entity. Returns
 * `undefined` for a manual binding (the sender supplies it) or when the linked
 * entity / field value is absent — the caller then knows to prompt for it rather
 * than send an empty value. `orgName` is passed in because the conversation only
 * carries the organization id, not its name. Computed reservation fields
 * (check-in date/time...) read the server-localized value for `language`.
 */
export function resolveBinding(
  binding: TemplateVariableBinding | undefined,
  conversation: BindingContext,
  orgName: string,
  language?: string,
): string | undefined {
  if (!binding || binding.source === bindingSourceEnum.MANUAL) return undefined
  const key = snakeToCamel(binding.field ?? '')
  switch (binding.source) {
    case bindingSourceEnum.CONTACT:
      return nonEmpty((conversation.contactRef as Record<string, unknown> | null)?.[key])
    case bindingSourceEnum.RESERVATION: {
      const reservation = conversation.reservationRef
      const localized = localizedFor(reservation?.localizedFields, language)?.[key]
      if (localized !== undefined) return nonEmpty(localized)
      return nonEmpty((reservation as Record<string, unknown> | null)?.[key])
    }
    case bindingSourceEnum.GUEST:
      return nonEmpty((conversation.guestRef as Record<string, unknown> | null)?.[key])
    case bindingSourceEnum.ORGANIZATION:
      return binding.field === 'name' ? nonEmpty(orgName) : undefined
    case bindingSourceEnum.RESIDENCE:
      return nonEmpty(
        (conversation.reservationRef?.propertyRef as Record<string, unknown> | null)?.[key],
      )
    default:
      return undefined
  }
}
